import { FitAddon } from '@xterm/addon-fit';
import { Terminal } from '@xterm/xterm';
import '@xterm/xterm/css/xterm.css';
import { KeyRound, Loader2, PhoneOff, RefreshCw, SquareTerminal } from 'lucide-react';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router';
import { useScope } from '../scope/scope';
import { REMOTE_CHANNEL, useRecord } from '../search/hooks';
import { Logo, useNow } from '../ui/misc';
import { initialTerminal, type TerminalState, TerminalSession } from './terminal-session';

/**
 * A full-access PowerShell terminal (WRMM-68), in a window of its own: /w/:mspId/terminal/:deviceId. One key touch for
 * the session, then a live shell (as SYSTEM) until it is closed, the window closes, or the eight-hour limit. Everything
 * typed and shown is recorded with the session.
 */
export function TerminalPage() {
  const { deviceId } = useParams();
  const { mspId } = useScope();
  const [query] = useSearchParams();
  const requestedMode = query.get('mode');
  const terminalMode = requestedMode === 'support' || requestedMode === 'tmux' || requestedMode === 'tmux-view' ? requestedMode : 'shell';
  const device = useRecord('device', deviceId).data;
  const host = useRef<HTMLDivElement>(null);
  const session = useRef<TerminalSession | null>(null);
  const [state, setState] = useState<TerminalState>(initialTerminal);
  const [attempt, setAttempt] = useState(0);
  const now = useNow(1000);
  const title = device?.title ?? 'Device';
  const linux = device?.os === 'linux';
  const shell = terminalMode === 'support' ? 'Persistent support shell' : terminalMode === 'tmux-view' ? 'View tmux' : terminalMode === 'tmux' ? 'Shared tmux' : linux ? 'Bash' : 'PowerShell';
  const account = terminalMode.startsWith('tmux') ? 'session owner' : linux ? 'root' : 'SYSTEM';

  useEffect(() => {
    if (!deviceId || !host.current) return;
    const term = new Terminal({ cursorBlink: true, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace', fontSize: 13.5,
      scrollback: 5000, theme: { background: '#0a1620', foreground: '#d6e2ea', cursor: '#F07522', selectionBackground: '#28506b' } });
    const fit = new FitAddon();
    term.loadAddon(fit);
    term.open(host.current);
    fit.fit();
    const terminal = new TerminalSession({ mspId, deviceId, terminalMode, size: () => ({ cols: term.cols, rows: term.rows }), onChange: setState,
      onOutput: data => term.write(data) });
    const typed = term.onData(data => terminal.input(data));
    const resized = term.onResize(({ cols, rows }) => terminal.resize(cols, rows));
    const observer = new ResizeObserver(() => { try { fit.fit(); } catch { /* Not laid out yet. */ } });
    observer.observe(host.current);
    setState(initialTerminal);
    // Started on the next tick: React's development double-mount runs this cleanup first, so only one session opens.
    const timer = setTimeout(() => { session.current = terminal; void terminal.start(); }, 0);
    const leave = () => terminal.end();
    window.addEventListener('pagehide', leave);
    return () => {
      clearTimeout(timer); window.removeEventListener('pagehide', leave); observer.disconnect(); typed.dispose(); resized.dispose();
      terminal.end(); term.dispose(); if (session.current === terminal) session.current = null;
    };
  }, [mspId, deviceId, terminalMode, attempt]);

  useEffect(() => { document.title = `${title} · ${shell} · WITTCO Remote`; }, [title, shell]);
  // Tells the console's device page (in another window) that a session started or ended.
  useEffect(() => {
    if ((state.phase !== 'connected' && state.phase !== 'ended') || typeof BroadcastChannel === 'undefined') return;
    const channel = new BroadcastChannel(REMOTE_CHANNEL);
    channel.postMessage({ deviceId });
    const again = setTimeout(() => channel.postMessage({ deviceId }), 2000);
    return () => { clearTimeout(again); channel.close(); };
  }, [state.phase, deviceId]);
  // Focus goes to the terminal as soon as it is live.
  useEffect(() => { if (state.phase === 'connected') host.current?.querySelector('textarea')?.focus(); }, [state.phase]);

  const elapsed = state.connectedAt ? Math.max(0, Math.floor((now - state.connectedAt) / 1000)) : 0;
  const clock = `${Math.floor(elapsed / 3600)}:${String(Math.floor(elapsed / 60) % 60).padStart(2, '0')}:${String(elapsed % 60).padStart(2, '0')}`;
  const [tone, label] = state.phase === 'connected' ? ['bg-[#16a34a]', clock] : state.phase === 'ended' ? ['bg-[#64748b]', 'Ended']
    : state.phase === 'approving' ? ['bg-[#F07522]', 'Waiting for your key'] : ['bg-[#eab308]', 'Connecting'];

  return (
    <div className="flex h-dvh flex-col bg-[#0a1620] text-[#d6e2ea]">
      <header className="flex h-11 shrink-0 items-center gap-2 border-b border-white/10 bg-[#0d1d2a] px-2 sm:px-3">
        <Logo className="size-6 shrink-0" />
        <div className="min-w-0">
          <p className="truncate font-mono text-[13px] font-semibold text-white">{title}</p>
          <p className="truncate text-[11px] text-[#8fa3b3]">{shell} · {device?.organizationName ?? 'WITTCO Remote'}</p>
        </div>
        <span className="ml-2 inline-flex items-center gap-1.5 rounded-full bg-white/5 px-2 py-0.5 text-[11.5px] tabular">
          <span className={`size-2 rounded-full ${tone}`} />{label}
        </span>
        <span className="flex-1" />
        <span className="hidden text-[11.5px] text-[#8fa3b3] md:inline">Recorded · as {account}</span>
        {state.phase !== 'ended' && (
          <button type="button" onClick={() => session.current?.end()}
            className="ml-2 inline-flex h-8 items-center gap-1.5 rounded-md bg-[#c2410c] px-2.5 text-[12.5px] font-semibold text-white hover:bg-[#d4500f]">
            <PhoneOff className="size-3.5" /><span className="hidden sm:inline">Close</span>
          </button>
        )}
      </header>
      {state.notice && state.phase !== 'ended' && (
        <p role="status" className="shrink-0 border-b border-white/10 bg-[#163047] px-4 py-1.5 text-center text-[12.5px]">{state.notice}</p>
      )}
      <div className="relative min-h-0 flex-1 p-2">
        <div ref={host} className="h-full w-full" data-testid="terminal" aria-label={`${shell} on ${title}`} />
        {state.phase === 'approving' && state.approval && (
          <Overlay>
            <span className="flex size-12 items-center justify-center rounded-full bg-[#F07522]/15 text-[#F07522]"><KeyRound className="size-6" /></span>
            <p className="mt-4 text-[16px] font-semibold text-white">Approve {shell} on {title}</p>
            <p className="mt-1.5 max-w-md text-[13.5px] text-[#b8c7d3]">One touch of your security key or passkey covers this whole session: every command runs
              as {account} without asking again, until you close it (at most eight hours). Everything typed and shown is recorded. {terminalMode !== 'shell' && 'Closing this viewer detaches; the tmux session and its commands keep running. View mode limits input to tmux but still requires full terminal permission.'}</p>
            {state.approval.error && <p role="alert" className="mt-3 max-w-md text-[13px] text-[#fca5a5]">{state.approval.error}</p>}
            <button type="button" disabled={state.approval.busy} onClick={() => void session.current?.approve()}
              className="mt-5 inline-flex h-9 items-center gap-2 rounded-lg bg-[#F07522] px-3.5 text-sm font-semibold text-white hover:brightness-105 disabled:opacity-60">
              <KeyRound className="size-4" />{state.approval.busy ? 'Waiting for your key…' : 'Approve with your key'}</button>
          </Overlay>
        )}
        {(state.phase === 'starting' || state.phase === 'connecting') && (
          <Overlay><Loader2 className="size-6 animate-spin text-[#8fa3b3]" /><p className="mt-3 text-[13.5px] text-[#b8c7d3]">
            {state.phase === 'starting' ? 'Asking for a session…' : `Starting ${shell} on the device…`}</p></Overlay>
        )}
        {state.phase === 'ended' && state.ended && (
          <Overlay>
            <span className="flex size-12 items-center justify-center rounded-full bg-white/5"><SquareTerminal className="size-6" /></span>
            <p className="mt-4 text-[16px] font-semibold text-white">{state.ended.reason === 'refused' ? 'The terminal didn’t start' : 'Terminal closed'}</p>
            <p className="mt-1.5 max-w-md text-[13.5px] text-[#b8c7d3]">{state.ended.message}</p>
            <button type="button" onClick={() => setAttempt(a => a + 1)}
              className="mt-5 inline-flex h-9 items-center gap-2 rounded-lg bg-white/10 px-3.5 text-sm font-medium hover:bg-white/15">
              <RefreshCw className="size-4" />Open again</button>
          </Overlay>
        )}
      </div>
    </div>
  );
}

function Overlay({ children }: { children: ReactNode }) {
  return <div className="absolute inset-0 flex flex-col items-center justify-center bg-[#0a1620]/90 p-6 text-center">{children}</div>;
}
